import React, { useState, useEffect } from 'react';
import { Button, Frame, SageTokens } from '@kajabi/sage-react';

import type { MessageType } from 'common/components/CopyMagic/contexts/MessagesContext/MessagesContext.types';
import { useUiResourceContext } from 'common/components/CopyMagic/contexts';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import postMessageRatings from 'common/components/CopyMagic/api/postMessageRatings';

const noop = () => {};

/*
MessageFeedbackSentiment
  Sends product analytics tracking event on click
  It allows the user to 'change their mind' within a grace period time window
  Allows only 1 product analytics tracking event to occur during the lifetime of the component
*/

type Props = {
  message: MessageType;
};

const TRACK_FEEDBACK_GRACEPERIOD = 30 * 1000; // 30 seconds

const MessageFeedbackSentiment = ({ message }: Props) => {
  const { loading } = useUiResourceContext();
  const [positiveFeedback, setPositiveFeedback] = useState<null | boolean>(null);
  const [hasBeenTracked, setHasBeenTracked] = useState<boolean>(false);
  const trackFeedback = useGeneratorTrackingCallback('useTrackMessageFeedbackSentiment');

  useEffect(() => {
    if (hasBeenTracked) return noop;
    if (positiveFeedback === null) return noop;

    const timer = setTimeout(() => {
      trackFeedback({ message, positiveFeedback });
      setHasBeenTracked(true);
      postMessageRatings({ message, positiveFeedback });
    }, TRACK_FEEDBACK_GRACEPERIOD);

    return () => clearTimeout(timer);
  }, [positiveFeedback]); // eslint-disable-line react-hooks/exhaustive-deps

  const thumbUpIcon =
    positiveFeedback === true ? SageTokens.ICONS.THUMB_UP_FILLED : SageTokens.ICONS.THUMB_UP;

  const thumbDownIcon =
    positiveFeedback === false ? SageTokens.ICONS.THUMB_DOWN_FILLED : SageTokens.ICONS.THUMB_DOWN;

  // Disabled if loading or a tracking event has been triggered
  const disabled = loading || hasBeenTracked;

  return (
    <Frame
      align={Frame.ALIGNMENTS.CENTER_SPREAD}
      direction={Frame.DIRECTIONS.HORIZONTAL}
      gap={Frame.GAPS.SM}
      width={Frame.WIDTHS.HUG}
    >
      <Button
        data-testid="thumbs-up"
        disabled={disabled}
        icon={thumbUpIcon}
        iconOnly
        onClick={() => setPositiveFeedback(true)}
        selected
        small
        subtle
      />
      <Button
        data-testid="thumbs-down"
        disabled={disabled}
        icon={thumbDownIcon}
        iconOnly
        onClick={() => setPositiveFeedback(false)}
        selected
        small
        subtle
      />
    </Frame>
  );
};

export default MessageFeedbackSentiment;
